import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, makeStyles, Tabs } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';

import {
  SearchSortFieldsV2,
  SegmentStatus,
  useGetUserSearchesV2Query,
  useGetUserSegmentsQuery,
  UserStatus,
} from 'api';
import { LargeTab } from 'components';
import { AdminContentEmailCreate } from 'Links';
import { useContentEmailHistory, useContentEmailSpecs } from 'features/admin/hooks';
import { useNotify } from 'hooks';

import {
  CONTENT_EMAIL_NON_RECURRING_VARIABLES,
  CONTENT_EMAIL_RECURRING_VARIABLES,
  CONTENT_EMAIL_SENT_VARIABLES,
} from 'features/admin/constants';

import ScheduledEmails from '../components/Emails/Scheduled';
import SentEmails from '../components/Emails/Sent';
import Loader from '../components/Emails/Loader';

type TabValues = 'recurring' | 'non-recurring' | 'sent';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(4),
  },
}));

const USER_SEARCH_VARIABLES = {
  q: '',
  size: 1,
  sort: SearchSortFieldsV2.FullName,
  user_status: UserStatus.Active,
};

const Emails: FC = () => {
  const [tab, setTab] = useState<TabValues>('recurring');
  const classes = useStyles();
  const notify = useNotify();
  const { t } = useTranslation();

  const userSegmentsQuery = useGetUserSegmentsQuery(
    { segment_status: SegmentStatus.Active },
    {
      onError: notify.queryError,
    }
  );

  const userSearchesQuery = useGetUserSearchesV2Query(USER_SEARCH_VARIABLES, {
    enabled: canLoadUserSearches(),
  });

  const recurringQuery = useContentEmailSpecs(CONTENT_EMAIL_RECURRING_VARIABLES, {
    enabled: canLoadRecurring(),
  });

  const nonRecurringQuery = useContentEmailSpecs(CONTENT_EMAIL_NON_RECURRING_VARIABLES, {
    enabled: canLoadNonRecurring(),
  });

  const sentQuery = useContentEmailHistory(CONTENT_EMAIL_SENT_VARIABLES, {
    enabled: canLoadSent(),
  });

  function canLoadUserSearches() {
    return !!userSegmentsQuery?.data?.getUserSegments;
  }

  function canLoadRecurring() {
    return (
      !!userSegmentsQuery?.data?.getUserSegments &&
      !!userSearchesQuery?.data?.getUserSearchesV2 &&
      tab === 'recurring'
    );
  }

  function canLoadNonRecurring() {
    return (
      !!userSegmentsQuery?.data?.getUserSegments &&
      !!userSearchesQuery?.data?.getUserSearchesV2 &&
      tab === 'non-recurring'
    );
  }

  function canLoadSent() {
    return !!userSegmentsQuery?.data?.getUserSegments && tab === 'sent';
  }

  function isSegmentsLoaded() {
    return !!userSegmentsQuery?.data?.getUserSegments;
  }

  function isUserSearchesLoaded() {
    return !!userSearchesQuery?.data?.getUserSearchesV2;
  }

  function isRecurringLoaded() {
    return isSegmentsLoaded() && isUserSearchesLoaded() && !recurringQuery?.isInitialLoad;
  }

  function isNonRecurringLoaded() {
    return isSegmentsLoaded() && isUserSearchesLoaded() && !nonRecurringQuery?.isInitialLoad;
  }

  function isSentLoaded() {
    return isSegmentsLoaded() && isUserSearchesLoaded() && !sentQuery?.isInitialLoad;
  }

  function handleTabChange(event: ChangeEvent<HTMLDivElement>, value: TabValues) {
    setTab(value);
  }

  function renderRecurringEmails() {
    if (!isRecurringLoaded()) {
      return <Loader rows={5} />;
    }

    const { getUserSegments } = userSegmentsQuery.data;
    const { total_count } = userSearchesQuery.data.getUserSearchesV2;
    const { sentinelRef, results, isExhausted } = recurringQuery;

    return (
      <ScheduledEmails
        variables={CONTENT_EMAIL_RECURRING_VARIABLES}
        segments={getUserSegments}
        numActiveUsers={total_count}
        isExhausted={isExhausted}
        sentinelRef={sentinelRef}
        scheduledEmails={results}
      />
    );
  }

  function renderNonRecurringEmails() {
    if (!isNonRecurringLoaded()) {
      return <Loader rows={5} />;
    }

    const { getUserSegments } = userSegmentsQuery.data;
    const { total_count } = userSearchesQuery.data.getUserSearchesV2;
    const { sentinelRef, results, isExhausted } = nonRecurringQuery;

    return (
      <ScheduledEmails
        variables={CONTENT_EMAIL_NON_RECURRING_VARIABLES}
        segments={getUserSegments}
        numActiveUsers={total_count}
        isExhausted={isExhausted}
        sentinelRef={sentinelRef}
        scheduledEmails={results}
      />
    );
  }

  function renderSentEmailList() {
    if (!isSentLoaded()) {
      return <Loader rows={5} />;
    }

    const { getUserSegments } = userSegmentsQuery.data;
    const { sentinelRef, results, isExhausted } = sentQuery;

    return (
      <SentEmails
        segments={getUserSegments}
        isExhausted={isExhausted}
        sentinelRef={sentinelRef}
        sentEmails={results}
      />
    );
  }

  return (
    <Fragment>
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <Tabs value={tab} onChange={handleTabChange}>
            <LargeTab label={t('common:recurring')} value="recurring" />
            <LargeTab label={t('common:nonRecurring')} value="non-recurring" />
            <LargeTab label={t('common:sent')} value="sent" />
          </Tabs>
        </Grid>
        <Grid item>
          <Button
            color="primary"
            variant="contained"
            component={AdminContentEmailCreate}
            startIcon={<EmailIcon />}
          >
            {t('contentEmails:newEmail')}
          </Button>
        </Grid>
      </Grid>
      <div className={classes.root}>
        {tab === 'recurring' && renderRecurringEmails()}
        {tab === 'non-recurring' && renderNonRecurringEmails()}
        {tab === 'sent' && renderSentEmailList()}
      </div>
    </Fragment>
  );
};

export default Emails;
